if (typeof QuickView === "undefined") {
  const { FoxThemeStyles, FoxThemeScripts } = window;

  class QuickView {
    constructor() {
      this.modal = null;
      this.mediaGallery = null;
      this.target = null;
      this.requestId = 0;
      this.initialMediaTimers = [];
      this.initialMediaInteractionHandler = null;

      addEventDelegate({
        selector: "[data-product-quickview]",
        handler: (event, target) => {
          event.preventDefault();

          const productHandle = target.dataset.productQuickview;
          if (!productHandle) return;

          target.classList.add("btn--loading");
          this.fetchHtml(productHandle, target);
        },
      });

      window.FoxThemeEvents.subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        if (this.modal) this.modal.hide();
      });
    }

    fetchHtml(productHandle, target) {
      const currentRequestId = ++this.requestId;
      this.target = target;

      loadAssets(
        [FoxThemeStyles.product, FoxThemeStyles.volumePricing],
        "quick-view-assets"
      );

      fetchSection("product-quickview", {
        url: `${window.FoxThemeSettings.base_url}products/${productHandle}`,
      })
        .then((html) => {
          if (currentRequestId !== this.requestId) {
            target.classList.remove("btn--loading");
            return;
          }

          const modal = html.querySelector("modal-dialog");
          if (!modal) {
            throw new Error("Quick View modal markup was not returned.");
          }

          const firstModel = html.querySelector("product-model");

          this.removeCurrentModal();
          this.modal = modal;
          this.mediaGallery = this.modal.querySelector("media-gallery");
          document.body.appendChild(this.modal);

          loadAssets(
            [
              FoxThemeScripts.productInfo,
              FoxThemeScripts.productMedia,
              FoxThemeScripts.variantsPicker,
              FoxThemeScripts.volumePricing,
            ],
            "variants-picker",
            () => {
              if (
                currentRequestId !== this.requestId ||
                !this.modal ||
                !this.modal.isConnected
              ) {
                target.classList.remove("btn--loading");
                return;
              }

              this.modal.show(target);

              document.dispatchEvent(
                new CustomEvent("quick-view:open", {
                  detail: { productUrl: `/products/${productHandle}` },
                })
              );

              if (this.mediaGallery) {
                const thumbnails = this.mediaGallery.querySelector(
                  '[id^="GalleryThumbnails"]'
                );

                if (thumbnails && typeof thumbnails.update === "function") {
                  thumbnails.update();
                }

                if (typeof this.mediaGallery.update === "function") {
                  this.mediaGallery.update();
                }

                this.resetInitialMedia(this.mediaGallery);
              }

              if (window.Shopify?.PaymentButton) {
                window.Shopify.PaymentButton.init();
              }

              target.classList.remove("btn--loading");
              this.handleClose();

              this.toolTip =
                typeof window.__reInitTooltip === "function"
                  ? window.__reInitTooltip(this.modal)
                  : [];

              document.dispatchEvent(
                new CustomEvent("quick-view:loaded", {
                  detail: { productUrl: `/products/${productHandle}` },
                })
              );
            }
          );

          if (firstModel) {
            loadAssets(
              [
                FoxThemeScripts.productModel,
                "https://cdn.shopify.com/shopifycloud/model-viewer-ui/assets/v1.0/model-viewer-ui.css",
              ],
              "product-model-assets"
            );
          }
        })
        .catch((error) => {
          target.classList.remove("btn--loading");
          console.error("Quick View failed to load:", error);
        });
    }

    resetInitialMedia(mediaGallery) {
      this.clearInitialMediaReset();

      if (
        !mediaGallery ||
        mediaGallery.dataset.forceFirstMedia !== "true"
      ) {
        return;
      }

      let customerInteracted = false;

      this.initialMediaInteractionHandler = () => {
        customerInteracted = true;
        this.clearInitialMediaReset();
      };

      ["pointerdown", "keydown", "change"].forEach((eventName) => {
        this.modal.addEventListener(
          eventName,
          this.initialMediaInteractionHandler,
          { capture: true, once: true }
        );
      });

      // The gallery, thumbnail slider and variant picker are upgraded
      // asynchronously. Re-selecting index 0 during this short initialisation
      // window prevents a default variant image from replacing the first media.
      [0, 50, 120, 250, 450, 750, 1100, 1600].forEach((delay) => {
        const timer = window.setTimeout(() => {
          if (
            customerInteracted ||
            !this.modal?.isConnected ||
            !mediaGallery.isConnected
          ) {
            return;
          }

          this.selectFirstMedia(mediaGallery);
        }, delay);

        this.initialMediaTimers.push(timer);
      });
    }

    selectFirstMedia(mediaGallery) {
      const mediaSliderElement = mediaGallery.querySelector(
        '[id^="Media-Gallery"]'
      );
      const thumbnailSliderElement = mediaGallery.querySelector(
        '[id^="GalleryThumbnails"]'
      );

      const mediaSlider = mediaSliderElement?.slider?.instance;
      const thumbnailSlider = thumbnailSliderElement?.slider?.instance;

      if (!mediaSlider || !mediaSlider.cells?.length) return false;

      mediaSlider.select(0, false, true);

      if (thumbnailSlider?.cells?.length) {
        thumbnailSlider.select(0, false, true);
      }

      const counter = mediaGallery.querySelector(
        ".flickity-counter--current"
      );
      if (counter) counter.textContent = "1";

      return mediaSlider.selectedIndex === 0;
    }

    clearInitialMediaReset() {
      this.initialMediaTimers.forEach((timer) => window.clearTimeout(timer));
      this.initialMediaTimers = [];

      if (this.modal && this.initialMediaInteractionHandler) {
        ["pointerdown", "keydown", "change"].forEach((eventName) => {
          this.modal.removeEventListener(
            eventName,
            this.initialMediaInteractionHandler,
            true
          );
        });
      }

      this.initialMediaInteractionHandler = null;
    }

    removeCurrentModal() {
      this.clearInitialMediaReset();

      if (this.modal?.isConnected) {
        this.modal.remove();
      }

      this.modal = null;
      this.mediaGallery = null;
    }

    handleClose() {
      if (!this.modal) return;

      const modal = this.modal;

      modal.addEventListener(
        "close",
        () => {
          this.clearInitialMediaReset();

          const allTooltip = [
            ...(modal.querySelector("product-info")?.toolTip || []),
            ...(this.toolTip || []),
          ];

          allTooltip.forEach((item) => {
            if (item && typeof item.unmount === "function") item.unmount();
          });

          modal.querySelectorAll("quantity-input").forEach((input) => {
            if (typeof input.disconnectedCallback === "function") {
              input.disconnectedCallback();
            }
          });

          modal.remove();

          if (this.modal === modal) {
            this.modal = null;
            this.mediaGallery = null;
          }
        },
        { once: true }
      );
    }
  }

  new QuickView();
  window.QuickView = QuickView;
}
